export const ASSISTANT_PAGE_IDS = [
  'lobby',
  'matchmaking',
  'deck_selection',
  'game',
  'catalog',
  'sandbox',
  'onboarding_deck',
  'deck_library',
  'deck_editor',
  'unknown',
] as const;

export type AssistantPageId = (typeof ASSISTANT_PAGE_IDS)[number];

export interface AssistantChatMessage {
  readonly role: 'user' | 'assistant';
  readonly content: string;
}

export interface AssistantDeckContext {
  readonly deckId: string;
  readonly name: string;
  readonly cardIds: readonly string[];
  readonly passiveId: string | null;
}

export interface AssistantChatRequest {
  readonly messages: readonly AssistantChatMessage[];
  readonly page: AssistantPageId;
  readonly visibleElementIds: readonly string[];
  readonly deck?: AssistantDeckContext;
  readonly sandbox?: AssistantSandboxContext;
}

export interface AssistantDeckSuggestion {
  readonly title: string;
  readonly summary: string;
  readonly cardIds: readonly string[];
  readonly passiveId: string | null;
}

export interface AssistantCatalogAction {
  readonly query: string;
  readonly cardIds: readonly string[];
}

export type AssistantSandboxSide = 'A' | 'B';
export type AssistantSandboxZone = 'board' | 'hand' | 'armed';

export interface AssistantSandboxCardContext {
  readonly instanceId: string;
  readonly defId: string;
  readonly ownerSide: AssistantSandboxSide;
  readonly zone: AssistantSandboxZone;
  readonly cellId: string | null;
}

export interface AssistantSandboxPlayerContext {
  readonly side: AssistantSandboxSide;
  readonly health: number;
  readonly energy: number;
  readonly energyMax: number;
  readonly temporaryEnergy: number;
  readonly passiveId: string | null;
}

/** Redacted-free personal sandbox setup sent only from the sandbox screen. */
export interface AssistantSandboxContext {
  readonly cards: readonly AssistantSandboxCardContext[];
  readonly players: readonly AssistantSandboxPlayerContext[];
  readonly attackProhibition: 'none' | AssistantSandboxSide | 'all';
}

export interface AssistantSandboxCardPlacement {
  readonly defId: string;
  readonly ownerSide: AssistantSandboxSide;
  readonly zone: AssistantSandboxZone;
  readonly cellId: string | null;
}

export interface AssistantSandboxPlayerPatch {
  readonly side: AssistantSandboxSide;
  readonly health: number | null;
  readonly energy: number | null;
  readonly energyMax: number | null;
  readonly temporaryEnergy: number | null;
}

export interface AssistantSandboxPassivePatch {
  readonly side: AssistantSandboxSide;
  readonly passiveId: string | null;
}

/** Bounded setup proposal. The browser still revalidates it through the engine. */
export interface AssistantSandboxAction {
  readonly replace: boolean;
  readonly cards: readonly AssistantSandboxCardPlacement[];
  readonly removeCardIds: readonly string[];
  readonly players: readonly AssistantSandboxPlayerPatch[];
  readonly passives: readonly AssistantSandboxPassivePatch[];
  readonly attackProhibition: 'none' | AssistantSandboxSide | 'all' | null;
}

export interface AssistantReply {
  readonly message: string;
  readonly focusElementId: string | null;
  readonly catalogAction: AssistantCatalogAction | null;
  readonly deckSuggestion: AssistantDeckSuggestion | null;
  readonly sandboxAction: AssistantSandboxAction | null;
}

export interface AssistantChatResponse {
  readonly reply: AssistantReply;
  readonly mode: 'ui' | 'deck';
}

const PAGE_IDS = new Set<string>(ASSISTANT_PAGE_IDS);
const MAX_MESSAGES = 24;
const MAX_MESSAGE_LENGTH = 4_000;
const MAX_ELEMENT_IDS = 32;
const MAX_CARD_IDS = 12;
const MAX_CATALOG_CARD_IDS = 20;
const MAX_SANDBOX_CARDS = 64;

function exactObject(
  value: unknown,
  keys: readonly string[],
  label: string,
): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new SyntaxError(`${label}: expected object`);
  }
  const object = value as Record<string, unknown>;
  const actual = Object.keys(object);
  if (actual.length !== keys.length || actual.some((key) => !keys.includes(key))) {
    throw new SyntaxError(`${label}: invalid fields`);
  }
  return object;
}

function boundedString(
  value: unknown,
  label: string,
  maximum: number,
  allowEmpty = false,
): string {
  if (typeof value !== 'string'
    || (!allowEmpty && value.trim().length === 0)
    || value.length > maximum
    || /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/u.test(value)) {
    throw new SyntaxError(`${label}: invalid string`);
  }
  return value;
}

function stringArray(
  value: unknown,
  label: string,
  maximumItems: number,
  maximumLength: number,
): readonly string[] {
  if (!Array.isArray(value) || value.length > maximumItems) {
    throw new SyntaxError(`${label}: invalid array`);
  }
  return Object.freeze(value.map((item, index) =>
    boundedString(item, `${label}[${index}]`, maximumLength)));
}

function parseMessages(value: unknown): readonly AssistantChatMessage[] {
  if (!Array.isArray(value) || value.length < 1 || value.length > MAX_MESSAGES) {
    throw new SyntaxError('messages: invalid array');
  }
  return Object.freeze(value.map((item, index) => {
    const object = exactObject(item, ['role', 'content'], `messages[${index}]`);
    if (object.role !== 'user' && object.role !== 'assistant') {
      throw new SyntaxError(`messages[${index}].role: invalid role`);
    }
    return Object.freeze({
      role: object.role,
      content: boundedString(
        object.content,
        `messages[${index}].content`,
        MAX_MESSAGE_LENGTH,
      ),
    });
  }));
}

function parseDeckContext(value: unknown): AssistantDeckContext {
  const object = exactObject(value, ['deckId', 'name', 'cardIds', 'passiveId'], 'deck');
  return Object.freeze({
    deckId: boundedString(object.deckId, 'deck.deckId', 64),
    name: boundedString(object.name, 'deck.name', 60),
    cardIds: stringArray(object.cardIds, 'deck.cardIds', MAX_CARD_IDS, 256),
    passiveId: nullableString(object.passiveId, 'deck.passiveId', 64),
  });
}

function sandboxSide(value: unknown, label: string): AssistantSandboxSide {
  if (value !== 'A' && value !== 'B') throw new SyntaxError(`${label}: invalid side`);
  return value;
}

function sandboxZone(value: unknown, label: string): AssistantSandboxZone {
  if (value !== 'board' && value !== 'hand' && value !== 'armed') {
    throw new SyntaxError(`${label}: invalid zone`);
  }
  return value;
}

function boundedInteger(value: unknown, label: string, minimum: number, maximum: number): number {
  if (!Number.isSafeInteger(value) || (value as number) < minimum || (value as number) > maximum) {
    throw new SyntaxError(`${label}: invalid integer`);
  }
  return value as number;
}

function parseSandboxContext(value: unknown): AssistantSandboxContext {
  const object = exactObject(value, ['cards', 'players', 'attackProhibition'], 'sandbox');
  if (!Array.isArray(object.cards) || object.cards.length > MAX_SANDBOX_CARDS) {
    throw new SyntaxError('sandbox.cards: invalid array');
  }
  if (!Array.isArray(object.players) || object.players.length !== 2) {
    throw new SyntaxError('sandbox.players: invalid array');
  }
  const cards = object.cards.map((item, index) => {
    const card = exactObject(item, ['instanceId', 'defId', 'ownerSide', 'zone', 'cellId'], `sandbox.cards[${index}]`);
    return Object.freeze({
      instanceId: boundedString(card.instanceId, `sandbox.cards[${index}].instanceId`, 64),
      defId: boundedString(card.defId, `sandbox.cards[${index}].defId`, 256),
      ownerSide: sandboxSide(card.ownerSide, `sandbox.cards[${index}].ownerSide`),
      zone: sandboxZone(card.zone, `sandbox.cards[${index}].zone`),
      cellId: card.cellId === null ? null : boundedString(card.cellId, `sandbox.cards[${index}].cellId`, 32),
    });
  });
  const players = object.players.map((item, index) => {
    const player = exactObject(item, ['side', 'health', 'energy', 'energyMax', 'temporaryEnergy', 'passiveId'], `sandbox.players[${index}]`);
    return Object.freeze({
      side: sandboxSide(player.side, `sandbox.players[${index}].side`),
      health: boundedInteger(player.health, `sandbox.players[${index}].health`, 0, 9_999),
      energy: boundedInteger(player.energy, `sandbox.players[${index}].energy`, 0, 9_999),
      energyMax: boundedInteger(player.energyMax, `sandbox.players[${index}].energyMax`, 0, 9_999),
      temporaryEnergy: boundedInteger(player.temporaryEnergy, `sandbox.players[${index}].temporaryEnergy`, 0, 9_999),
      passiveId: player.passiveId === null ? null : boundedString(player.passiveId, `sandbox.players[${index}].passiveId`, 64),
    });
  });
  const sides = players.map(({ side }) => side);
  if (new Set(sides).size !== 2) throw new SyntaxError('sandbox.players: duplicate side');
  if (object.attackProhibition !== 'none' && object.attackProhibition !== 'A'
    && object.attackProhibition !== 'B' && object.attackProhibition !== 'all') {
    throw new SyntaxError('sandbox.attackProhibition: invalid value');
  }
  return Object.freeze({ cards: Object.freeze(cards), players: Object.freeze(players), attackProhibition: object.attackProhibition });
}

export function parseAssistantChatRequest(value: unknown): AssistantChatRequest {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new SyntaxError('assistant request: expected object');
  }
  const raw = value as Record<string, unknown>;
  const expected = [
    'messages',
    'page',
    'visibleElementIds',
    ...(raw.deck === undefined ? [] : ['deck']),
    ...(raw.sandbox === undefined ? [] : ['sandbox']),
  ];
  const object = exactObject(value, expected, 'assistant request');
  const page = boundedString(object.page, 'page', 32);
  if (!PAGE_IDS.has(page)) throw new SyntaxError('page: invalid page');
  if (page !== 'deck_editor' && object.deck !== undefined) {
    throw new SyntaxError('deck context: only allowed in deck_editor');
  }
  if (page !== 'sandbox' && object.sandbox !== undefined) {
    throw new SyntaxError('sandbox context: only allowed in sandbox');
  }
  const messages = parseMessages(object.messages);
  if (messages.at(-1)?.role !== 'user') {
    throw new SyntaxError('messages: last message must be user');
  }
  return Object.freeze({
    messages,
    page: page as AssistantPageId,
    visibleElementIds: stringArray(
      object.visibleElementIds,
      'visibleElementIds',
      MAX_ELEMENT_IDS,
      64,
    ),
    ...(object.deck === undefined ? {} : { deck: parseDeckContext(object.deck) }),
    ...(object.sandbox === undefined ? {} : { sandbox: parseSandboxContext(object.sandbox) }),
  });
}

function nullableString(value: unknown, label: string, maximum: number): string | null {
  return value === null ? null : boundedString(value, label, maximum);
}

function parseDeckSuggestion(value: unknown): AssistantDeckSuggestion | null {
  if (value === null) return null;
  const object = exactObject(
    value,
    ['title', 'summary', 'cardIds', 'passiveId'],
    'deckSuggestion',
  );
  return Object.freeze({
    title: boundedString(object.title, 'deckSuggestion.title', 100),
    summary: boundedString(object.summary, 'deckSuggestion.summary', 1_500),
    cardIds: stringArray(object.cardIds, 'deckSuggestion.cardIds', MAX_CARD_IDS, 256),
    passiveId: nullableString(object.passiveId, 'deckSuggestion.passiveId', 64),
  });
}

function parseCatalogAction(value: unknown): AssistantCatalogAction | null {
  if (value === null) return null;
  const object = exactObject(value, ['query', 'cardIds'], 'catalogAction');
  return Object.freeze({
    query: boundedString(object.query, 'catalogAction.query', 120),
    cardIds: stringArray(
      object.cardIds,
      'catalogAction.cardIds',
      MAX_CATALOG_CARD_IDS,
      256,
    ),
  });
}

function nullableInteger(
  value: unknown,
  label: string,
  minimum: number,
  maximum: number,
): number | null {
  return value === null ? null : boundedInteger(value, label, minimum, maximum);
}

function parseSandboxAction(value: unknown): AssistantSandboxAction | null {
  if (value === null) return null;
  const object = exactObject(
    value,
    ['replace', 'cards', 'removeCardIds', 'players', 'passives', 'attackProhibition'],
    'sandboxAction',
  );
  if (typeof object.replace !== 'boolean') throw new SyntaxError('sandboxAction.replace: invalid boolean');
  if (!Array.isArray(object.cards) || object.cards.length > MAX_SANDBOX_CARDS) {
    throw new SyntaxError('sandboxAction.cards: invalid array');
  }
  if (!Array.isArray(object.players) || object.players.length > 2) {
    throw new SyntaxError('sandboxAction.players: invalid array');
  }
  if (!Array.isArray(object.passives) || object.passives.length > 2) {
    throw new SyntaxError('sandboxAction.passives: invalid array');
  }
  const cards = object.cards.map((item, index) => {
    const card = exactObject(item, ['defId', 'ownerSide', 'zone', 'cellId'], `sandboxAction.cards[${index}]`);
    return Object.freeze({
      defId: boundedString(card.defId, `sandboxAction.cards[${index}].defId`, 256),
      ownerSide: sandboxSide(card.ownerSide, `sandboxAction.cards[${index}].ownerSide`),
      zone: sandboxZone(card.zone, `sandboxAction.cards[${index}].zone`),
      cellId: card.cellId === null ? null : boundedString(card.cellId, `sandboxAction.cards[${index}].cellId`, 32),
    });
  });
  const players = object.players.map((item, index) => {
    const player = exactObject(item, ['side', 'health', 'energy', 'energyMax', 'temporaryEnergy'], `sandboxAction.players[${index}]`);
    return Object.freeze({
      side: sandboxSide(player.side, `sandboxAction.players[${index}].side`),
      health: nullableInteger(player.health, `sandboxAction.players[${index}].health`, 0, 9_999),
      energy: nullableInteger(player.energy, `sandboxAction.players[${index}].energy`, 0, 9_999),
      energyMax: nullableInteger(player.energyMax, `sandboxAction.players[${index}].energyMax`, 0, 9_999),
      temporaryEnergy: nullableInteger(player.temporaryEnergy, `sandboxAction.players[${index}].temporaryEnergy`, 0, 9_999),
    });
  });
  const passives = object.passives.map((item, index) => {
    const passive = exactObject(item, ['side', 'passiveId'], `sandboxAction.passives[${index}]`);
    return Object.freeze({
      side: sandboxSide(passive.side, `sandboxAction.passives[${index}].side`),
      passiveId: passive.passiveId === null ? null : boundedString(passive.passiveId, `sandboxAction.passives[${index}].passiveId`, 64),
    });
  });
  const attackProhibition = object.attackProhibition;
  if (attackProhibition !== null && attackProhibition !== 'none'
    && attackProhibition !== 'A' && attackProhibition !== 'B' && attackProhibition !== 'all') {
    throw new SyntaxError('sandboxAction.attackProhibition: invalid value');
  }
  return Object.freeze({
    replace: object.replace,
    cards: Object.freeze(cards),
    removeCardIds: stringArray(object.removeCardIds, 'sandboxAction.removeCardIds', MAX_SANDBOX_CARDS, 64),
    players: Object.freeze(players),
    passives: Object.freeze(passives),
    attackProhibition,
  });
}

export function parseAssistantReply(value: unknown): AssistantReply {
  const raw = typeof value === 'object' && value !== null && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
  const object = exactObject(
    value,
    raw.sandboxAction === undefined
      ? ['message', 'focusElementId', 'catalogAction', 'deckSuggestion']
      : ['message', 'focusElementId', 'catalogAction', 'deckSuggestion', 'sandboxAction'],
    'assistant reply',
  );
  return Object.freeze({
    message: boundedString(object.message, 'assistant reply.message', 6_000),
    focusElementId: nullableString(
      object.focusElementId,
      'assistant reply.focusElementId',
      64,
    ),
    catalogAction: parseCatalogAction(object.catalogAction),
    deckSuggestion: parseDeckSuggestion(object.deckSuggestion),
    sandboxAction: object.sandboxAction === undefined ? null : parseSandboxAction(object.sandboxAction),
  });
}

export function parseAssistantChatResponse(value: unknown): AssistantChatResponse {
  const object = exactObject(value, ['reply', 'mode'], 'assistant response');
  if (object.mode !== 'ui' && object.mode !== 'deck') {
    throw new SyntaxError('assistant response.mode: invalid mode');
  }
  return Object.freeze({
    reply: parseAssistantReply(object.reply),
    mode: object.mode,
  });
}
